/* global zxcvbn, alert */

import React, { useEffect, useState, useRef, FormEvent } from 'react';
import { Link, useLocation } from 'react-router';
import { connect } from 'react-redux';
import { user, stroot } from '@birdi/js-sdk';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import BirdiLogoSVG from '@birdi/icons/birdi-logo-navy.svg';
import OnboardingCreateAccountSVG from '@birdi/icons/onboarding-create-account-2.svg';
import PersonIcon from '@birdi/icons/onboarding-user.svg';
import EmailIcon from '@birdi/icons/onboarding-at.svg';
import LockIcon from '@birdi/icons/onboarding-lock.svg';
// @ts-ignore - JS file without types
import ErrorDisplay from '../../common/ErrorDisplay';
// @ts-ignore - JS file without types
import { updateField, completeInvitedSignup } from '../actions';
// @ts-ignore - JS file without types
import { Length } from '../../common/password/Length';
// @ts-ignore - JS file without types
import { LoadZXCVBN, ZXCVBNDisplay } from '../../common/password/zxcvbn';
import { ProgressBar } from './progress-bar';
import * as styles from './styles';
import { AuthState } from '../reducer';
import { LoadingSpinner } from '@birdi/loading';

interface InvitedUserFormProps {
  auth: AuthState;
  _updateField: (field: Partial<AuthState>) => void;
  _completeInvitedSignup: (obj: any) => Promise<void>;
}

const InvitedUserForm: React.FC<InvitedUserFormProps> = (props) => {
  const theme = useTheme() as BirdiTheme;
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [zxcvbnResult, setZxcvbnResult] = useState<any>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invitedEmail, setInvitedEmail] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [inviteUserId, setInviteUserId] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    // Get token from URL query parameters
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    const code = searchParams.get('code');
    const id = searchParams.get('id');

    // Support both token-based and code-based invitation links
    const invitationToken = token || code;

    if (!invitationToken) {
      setTokenError(
        'Invalid invitation link. Please check your email and try again.',
      );
      setIsLoading(false);
      return;
    }

    setInviteToken(invitationToken);
    if (id) {
      setInviteUserId(id);
    }

    // Fetch the invited user's email using the token
    user
      .getInvitedUserInfo(stroot('gum2os'), invitationToken, id)
      .then((res: any) => {
        if (res.body?.email) {
          setInvitedEmail(res.body.email);
          props._updateField({ email: res.body.email, error: null });
        } else {
          setTokenError(
            'Could not retrieve invitation details. The link may have expired.',
          );
        }
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.error('Error fetching invited user info:', err);
        if (err.status === 404 || err.status === 400) {
          setTokenError('This invitation link is invalid or has expired.');
        } else {
          setTokenError(
            'An error occurred. Please try again or contact support.',
          );
        }
        setIsLoading(false);
      });
  }, [location.search]);

  const setPasswordValue = (value: string) => {
    if (typeof (window as any).zxcvbn === 'function') {
      const zxcvbnResultValue = (window as any).zxcvbn(value, [invitedEmail]);
      setZxcvbnResult(zxcvbnResultValue);
    }
    setPassword(value);
  };

  const handleFullNameChange = (value: string) => {
    setFullName(value);
    // Split full name into first and last name
    const parts = value.trim().split(/\s+/);
    const fname = parts[0] || '';
    const lname = parts.slice(1).join(' ') || '';
    props._updateField({ fname, lname });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { _completeInvitedSignup, auth } = props;

    if (!formRef.current?.checkValidity()) {
      return formRef.current?.reportValidity();
    }

    if (!termsAccepted) {
      return alert('Please accept the Terms of Service and Privacy Policy');
    }

    if (password !== confirmPassword) {
      return alert('Passwords do not match');
    }

    if (zxcvbnResult && zxcvbnResult.score < 1) {
      return alert(
        `The password you're using is too weak. ${zxcvbnResult.feedback.suggestions[0]}`,
      );
    }

    if (!fullName.trim()) {
      return alert('Please enter your full name');
    }

    setIsSubmitting(true);
    props._updateField({ error: null });

    // Thunk catches failures and dispatches { error } to Redux (does not reject).
    await _completeInvitedSignup({
      token: inviteToken,
      ...(inviteUserId && { id: inviteUserId }),
      fname: auth.fname,
      lname: auth.lname,
      password,
    }).finally(() => setIsSubmitting(false));
  };

  if (isLoading) {
    return (
      <>
        <div css={styles.logoContainer}>
          <BirdiLogoSVG />
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            flex: 1;
            gap: 1rem;
          `}
        >
          <LoadingSpinner color="#2287FA" size="50px" />
          <p
            css={(theme: BirdiTheme) => css`
              color: ${theme.mono60};
              font-size: 1rem;
            `}
          >
            Loading your invitation...
          </p>
        </div>
      </>
    );
  }

  if (tokenError) {
    return (
      <>
        <div css={styles.logoContainer}>
          <BirdiLogoSVG />
        </div>
        <div css={styles.scrollableContent}>
          <div css={styles.headerSection}>
            <h1 css={styles.title}>Invitation Error</h1>
            <p
              css={css`
                color: #dc3545;
                font-size: 1rem;
                margin-top: 1rem;
              `}
            >
              {tokenError}
            </p>
            <div
              css={css`
                margin-top: 2rem;
              `}
            >
              <Link
                to="/sign-in"
                css={(theme: BirdiTheme) => css`
                  color: ${theme.deepOcean};
                  text-decoration: none;
                  font-weight: 500;
                  &:hover {
                    text-decoration: underline;
                  }
                `}
              >
                Go to Sign In
              </Link>
            </div>
          </div>
        </div>
        <div css={styles.fixedFooter}>
          <div css={styles.helpLink}>
            Need help? <a href="mailto:support@birdi.io">Contact support</a>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <LoadZXCVBN onLoad={() => {}} />

      {/* Logo - Fixed at top */}
      <div css={styles.logoContainer}>
        <BirdiLogoSVG />
      </div>

      {/* Scrollable Content */}
      <div css={styles.scrollableContent}>
        {/* Header Section */}
        <div css={styles.headerSection}>
          <div css={styles.iconContainer}>
            <div css={styles.iconWrapper(theme)}>
              <OnboardingCreateAccountSVG />
            </div>
            <h1 css={styles.title}>Complete your account</h1>
          </div>

          <ProgressBar currentStep={1} totalSteps={6} />

          <p css={styles.subtitle}>
            You've been invited to join Birdi. Complete your profile to get
            started.
          </p>
        </div>

        {/* Form */}
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          css={css`
            display: flex;
            flex-direction: column;
            gap: 0.25rem;

            @media (min-width: ${breakpoints.s}) {
              gap: 0.5rem;
            }
          `}
        >
          {/* Full Name */}
          <div css={styles.formField}>
            <label css={styles.label} htmlFor="fullName">
              Full name <span className="required">*</span>
            </label>
            <div css={styles.inputWrapper}>
              <div css={styles.inputIcon}>
                <PersonIcon />
              </div>
              <input
                id="fullName"
                type="text"
                css={styles.input}
                placeholder="Enter your full name"
                value={fullName}
                onChange={(event) => handleFullNameChange(event.target.value)}
                required
              />
            </div>
            <ErrorDisplay error={props.auth.error} path="fname" />
            <ErrorDisplay error={props.auth.error} path="lname" />
          </div>

          {/* Email - Prefilled and Disabled */}
          <div css={styles.formField}>
            <label css={styles.label} htmlFor="email">
              Email address
            </label>
            <div css={styles.inputWrapper}>
              <div css={styles.inputIcon}>
                <EmailIcon />
              </div>
              <input
                id="email"
                type="email"
                css={[
                  styles.input,
                  css`
                    background-color: #f5f5f5;
                    color: #666;
                    cursor: not-allowed;
                  `,
                ]}
                value={invitedEmail}
                disabled
              />
            </div>
            <p
              css={(theme: BirdiTheme) => css`
                font-size: 0.8125rem;
                color: ${theme.mono60};
                margin: 0.25rem 0 0 0;
              `}
            >
              This is the email address you were invited with.
            </p>
          </div>

          {/* Password */}
          <div css={styles.formField}>
            <label css={styles.label} htmlFor="password">
              Password <span className="required">*</span>
            </label>
            <div css={styles.inputWrapper}>
              <div css={styles.inputIcon}>
                <LockIcon />
              </div>
              <input
                id="password"
                type="password"
                css={styles.input}
                placeholder="Create a password"
                value={password}
                onChange={(event) => setPasswordValue(event.target.value)}
                required
                minLength={8}
              />
            </div>
            <Length password={password} />
            <ZXCVBNDisplay zxcvbnResult={zxcvbnResult} />
          </div>

          {/* Confirm Password */}
          <div css={styles.formField}>
            <label css={styles.label} htmlFor="confirmPassword">
              Confirm password <span className="required">*</span>
            </label>
            <div css={styles.inputWrapper}>
              <div css={styles.inputIcon}>
                <LockIcon />
              </div>
              <input
                id="confirmPassword"
                type="password"
                css={styles.input}
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                minLength={8}
              />
            </div>
            {confirmPassword && password !== confirmPassword && (
              <div css={styles.errorMessage}>Passwords do not match</div>
            )}
          </div>

          {/* Terms Checkbox */}
          <div css={styles.checkboxContainer}>
            <input
              id="terms"
              type="checkbox"
              css={styles.checkbox}
              checked={termsAccepted}
              onChange={(event) => setTermsAccepted(event.target.checked)}
              required
            />
            <label css={styles.checkboxLabel} htmlFor="terms">
              I agree to the{' '}
              <a
                href="https://www.birdi.io/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href="https://www.birdi.io/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
            </label>
          </div>

          <div
            css={(theme: BirdiTheme) => css`
              ${styles.errorMessage};
              a {
                color: ${theme.deepOcean};
                font-weight: 500;
              }
            `}
          >
            <ErrorDisplay error={props.auth.error} path="meta" />
          </div>

          {/* Submit Button */}
          <div css={styles.buttonContainer}>
            <button
              type="submit"
              css={styles.primaryButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <LoadingSpinner color="white" size="1em" />
              ) : (
                'Complete Setup'
              )}
            </button>
          </div>

          {/* Sign In Link */}
          <div
            css={(theme: BirdiTheme) => css`
              text-align: left;
              margin-top: 0.5rem;
              font-size: 0.9375rem;
              color: ${theme.mono70};
              flex-shrink: 0;
            `}
          >
            Already have an account?{' '}
            <Link
              to="/sign-in"
              css={(theme: BirdiTheme) => css`
                color: ${theme.deepOcean};
                text-decoration: none;
                font-weight: 500;

                &:hover {
                  text-decoration: underline;
                }
              `}
            >
              Sign in
            </Link>
          </div>
        </form>
      </div>

      {/* Fixed Footer - Help Link */}
      <div css={styles.fixedFooter}>
        <div css={styles.helpLink}>
          Need help? <a href="mailto:support@birdi.io">Contact support</a>
        </div>
      </div>
    </>
  );
};

export default connect(
  (state: any) => ({
    auth: state.auth,
  }),
  (dispatch: any) => ({
    _updateField: (field: Partial<AuthState>) => dispatch(updateField(field)),
    _completeInvitedSignup: (obj: any) => dispatch(completeInvitedSignup(obj)),
  }),
)(InvitedUserForm);
